import { Icon } from "@iconify/react";
import { type ChangeEvent, type ClipboardEvent, type DragEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { mentionToken, type TicketMentionCandidateDto, type TicketMentionCandidateGroupDto, TicketStatus, useTicketApi } from "@/api/tickets";
import { useSession } from "@/auth/session-provider";
import { Loader } from "@/components/motion/loader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { MessageValues, TranslationKey } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { identityLabelOf, initialsOf } from "@/utils/person-name";
import { useMentionCandidates } from "../hooks/use-mention-candidates";
import {
  attachmentIcon,
  fileInputAccept,
  formatFileSize,
  isAcceptedFileName,
  isPreviewableAttachment,
  localAttachmentView,
  maxAttachmentCount,
  maxAttachmentSize,
  type TicketAttachmentView,
} from "../utils/ticket-attachments";
import { reconcileTicketMentionOccurrences, serializeTicketMentions, type TicketMentionOccurrence } from "../utils/ticket-mentions";
import { AttachmentPreviewDialog } from "./attachment-preview-dialog";

const longPasteCharacterLimit = 4000;
const longPasteLineLimit = 80;
const mentionPattern = /(^|[\s([{"'，。！？、；：])@([\p{L}\p{N}_-]*)$/u;

/**
 * 编辑器里的一个附件。文件在**加入时就开始上传**，而不是等到点发送：
 * 传的时候能看见进度、失败能单独重试、发送前还能撤掉，而发送本身只带几个 id，一瞬间的事。
 */
type QueuedAttachment = {
  /** 上传成功后的服务端附件 id；发消息时带的就是它。 */
  attachmentId: string | null;
  /** 用于中止在途上传——撤掉一个正在传的文件不该等它传完。 */
  controller: AbortController | null;
  error: string | null;
  file: File;
  key: string;
  progress: number;
  status: "failed" | "ready" | "uploading";
  view: TicketAttachmentView;
};

type MentionMatch = {
  end: number;
  query: string;
  start: number;
};

export type InternalNoteDraft = {
  /** 已上传附件的服务端 id，随消息一起提交。 */
  attachmentIds: string[];
  /** 同一批附件的本地展示模型；乐观渲染直接用，不必等服务端回元数据。 */
  attachments: TicketAttachmentView[];
  content: string;
  /** 本次提及到的成员。乐观渲染直接拿这里的显示名，不用等服务端解析回来。 */
  mentions: Array<{ agentId: string; displayName: string }>;
};

/** 未编组的成员没有 teamName，单独给个标题；工单当前所属组标出来，方便优先挑人。 */
type Translate = (key: TranslationKey, values?: MessageValues) => string;

function groupLabel(group: TicketMentionCandidateGroupDto, t: Translate): string {
  if (!group.teamName) {
    return t("tickets.composer.mention.ungrouped");
  }
  return group.isAssignedTeam ? t("tickets.composer.mention.currentTeam", { team: group.teamName }) : group.teamName;
}

function candidateBadge(candidate: TicketMentionCandidateDto, t: Translate): string | null {
  if (candidate.isAssignee) return t("tickets.fields.assignee");
  if (candidate.isParticipant) return t("tickets.fields.participants");
  return null;
}

function findMention(value: string, cursor: number): MentionMatch | null {
  const match = value.slice(0, cursor).match(mentionPattern);
  const query = match?.[2];
  if (query === undefined) return null;

  return {
    start: cursor - query.length - 1,
    end: cursor,
    query,
  };
}

/** 只高亮仍然完整的提及区间；手动改过的提及会立即降级为普通文本。 */
function renderHighlightedContent(content: string, occurrences: readonly TicketMentionOccurrence[], rangeStart = 0, rangeEnd = content.length) {
  const parts: React.ReactNode[] = [];
  let cursor = rangeStart;
  for (const occurrence of occurrences) {
    if (occurrence.end <= rangeStart || occurrence.start >= rangeEnd) continue;
    const mentionStart = Math.max(occurrence.start, rangeStart);
    const mentionEnd = Math.min(occurrence.end, rangeEnd);
    if (mentionStart > cursor) parts.push(content.slice(cursor, mentionStart));
    parts.push(
      <span className="text-primary" key={`${occurrence.agentId}-${occurrence.start}-${mentionStart}`}>
        {content.slice(mentionStart, mentionEnd)}
      </span>,
    );
    cursor = mentionEnd;
  }
  if (cursor < rangeEnd) parts.push(content.slice(cursor, rangeEnd));
  return parts;
}

function renderComposerContent(content: string, occurrences: readonly TicketMentionOccurrence[], mentionAnchorPosition?: number) {
  if (mentionAnchorPosition === undefined) return renderHighlightedContent(content, occurrences);

  return (
    <>
      {renderHighlightedContent(content, occurrences, 0, mentionAnchorPosition)}
      <PopoverAnchor asChild>
        <span className="inline-block h-5 w-px align-bottom" />
      </PopoverAnchor>
      {renderHighlightedContent(content, occurrences, mentionAnchorPosition)}
    </>
  );
}

type InternalNoteComposerProps = {
  ticketId: string;
  status: TicketStatus;
  onSubmit: (draft: InternalNoteDraft) => Promise<void>;
  /** 嵌入容器据此与宿主协商上下文切换；独立端可以不传。 */
  onUnsavedWorkChange?: (hasUnsavedWork: boolean) => void;
};

export function InternalNoteComposer({ ticketId, status, onSubmit, onUnsavedWorkChange }: InternalNoteComposerProps) {
  const { intlLocale, t } = useI18n();
  const { agent } = useSession();
  const isReadOnly = status === TicketStatus.Closed || status === TicketStatus.Cancelled;
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<QueuedAttachment[]>([]);
  const [mention, setMention] = useState<MentionMatch | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [composerScrollTop, setComposerScrollTop] = useState(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<TicketAttachmentView | null>(null);
  const ticketApi = useTicketApi();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileDragDepthRef = useRef(0);
  const mentionAnchorPositionRef = useRef(0);
  const attachmentsRef = useRef<QueuedAttachment[]>([]);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(intlLocale), [intlLocale]);
  const percentFormatter = useMemo(() => new Intl.NumberFormat(intlLocale, { style: "percent", maximumFractionDigits: 0 }), [intlLocale]);

  // 每次选择都记录自己的文本区间与 agentId；姓名相同也能分别序列化。
  const [pickedMentions, setPickedMentions] = useState<TicketMentionOccurrence[]>([]);

  attachmentsRef.current = attachments;
  const updateAttachments = (update: (current: QueuedAttachment[]) => QueuedAttachment[]) => {
    setAttachments((current) => {
      const next = update(current);
      attachmentsRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    onUnsavedWorkChange?.(Boolean(content.trim()) || attachments.length > 0);
  }, [attachments.length, content, onUnsavedWorkChange]);

  useEffect(
    () => () => {
      // 宿主强制切换上下文时，停止半截上传并清掉已经上传但尚未随消息发送的草稿附件。
      for (const attachment of attachmentsRef.current) {
        attachment.controller?.abort();
        if (attachment.attachmentId) {
          void ticketApi.deleteAttachment(ticketId, attachment.attachmentId).catch(() => undefined);
        }
      }
      onUnsavedWorkChange?.(false);
    },
    [onUnsavedWorkChange, ticketApi, ticketId],
  );

  const { groups, flatCandidates, indexByAgentId, isLoading, isError, isSearching } = useMentionCandidates(
    ticketId,
    isReadOnly ? null : (mention?.query ?? null),
  );
  const duplicateCandidateNames = useMemo(() => {
    const countByName = new Map<string, number>();
    for (const candidate of flatCandidates) countByName.set(candidate.displayName, (countByName.get(candidate.displayName) ?? 0) + 1);
    return new Set([...countByName].filter(([, count]) => count > 1).map(([displayName]) => displayName));
  }, [flatCandidates]);

  // 候选随输入变化，旧的高亮下标可能越界。
  const activeIndex = Math.min(activeMentionIndex, Math.max(0, flatCandidates.length - 1));

  const updateMention = (value: string, cursor: number) => {
    const nextMention = findMention(value, cursor);
    if (nextMention?.start !== mention?.start || nextMention?.query !== mention?.query) setActiveMentionIndex(0);
    if (nextMention) mentionAnchorPositionRef.current = nextMention.start;
    setMention(nextMention);
  };

  const focusAt = (cursor: number) => {
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(cursor, cursor);
    });
  };

  const insertMention = (candidate: TicketMentionCandidateDto) => {
    if (!mention) return;

    const name = candidate.displayName;
    const nextContent = `${content.slice(0, mention.start)}@${name} ${content.slice(mention.end)}`;
    const nextCursor = mention.start + name.length + 2;
    const shiftedMentions = reconcileTicketMentionOccurrences(content, nextContent, pickedMentions);
    setPickedMentions(
      [...shiftedMentions, { agentId: candidate.agentId, displayName: name, start: mention.start, end: mention.start + name.length + 1 }].sort(
        (left, right) => left.start - right.start,
      ),
    );
    setContent(nextContent);
    setMention(null);
    focusAt(nextCursor);
  };

  const openMentionMenu = () => {
    const textarea = textareaRef.current;
    const selectionStart = textarea?.selectionStart ?? content.length;
    const selectionEnd = textarea?.selectionEnd ?? selectionStart;
    const beforeSelection = content.slice(0, selectionStart);
    const needsLeadingSpace = beforeSelection.length > 0 && !/[\s([{"'，。！？、；：]$/.test(beforeSelection);
    const prefix = needsLeadingSpace ? " @" : "@";
    const nextContent = `${beforeSelection}${prefix}${content.slice(selectionEnd)}`;
    const nextCursor = selectionStart + prefix.length;

    textarea?.focus();
    mentionAnchorPositionRef.current = nextCursor - 1;
    setPickedMentions((current) => reconcileTicketMentionOccurrences(content, nextContent, current));
    setContent(nextContent);
    setMention({ start: nextCursor - 1, end: nextCursor, query: "" });
    setActiveMentionIndex(0);
    requestAnimationFrame(() => textareaRef.current?.setSelectionRange(nextCursor, nextCursor));
  };

  const patchAttachment = (key: string, patch: Partial<QueuedAttachment>) =>
    updateAttachments((current) => current.map((attachment) => (attachment.key === key ? { ...attachment, ...patch } : attachment)));

  /**
   * 把文件送上去。失败只标在这一个附件上——一次传五个文件挂了一个，
   * 不该让另外四个和已经写好的正文陪葬。
   */
  const startUpload = async (entry: QueuedAttachment) => {
    if (isReadOnly) return;
    const controller = new AbortController();
    patchAttachment(entry.key, { status: "uploading", progress: 0, error: null, controller });

    try {
      const uploaded = await ticketApi.uploadAttachment(ticketId, entry.file, {
        signal: controller.signal,
        onProgress: (percent) => patchAttachment(entry.key, { progress: percent }),
      });
      patchAttachment(entry.key, { status: "ready", progress: 100, attachmentId: uploaded.id, controller: null });
    } catch (error) {
      // 主动撤掉时也会走到这里，那条附件已经从列表里删了，patch 无副作用。
      if (controller.signal.aborted) return;
      patchAttachment(entry.key, {
        status: "failed",
        controller: null,
        error: error instanceof Error && error.message ? error.message : t("tickets.composer.uploadFailed"),
      });
    }
  };

  const addFiles = (files: File[], source: "drop" | "long-text" | "picker" | "paste") => {
    if (isReadOnly || files.length === 0) return;

    const existingKeys = new Set(attachments.map((attachment) => attachment.key));
    const accepted: QueuedAttachment[] = [];
    const rejectedTypes: string[] = [];
    const oversizedFiles: string[] = [];
    let duplicateCount = 0;
    let limitReached = false;

    for (const file of files) {
      const view = localAttachmentView(file);
      if (existingKeys.has(view.key)) {
        duplicateCount += 1;
        continue;
      }
      // 前端这三道只是别让用户白等一次上传；真正的判定在服务端，两边清单同源于 ticket-attachments.ts。
      if (!isAcceptedFileName(file.name)) {
        rejectedTypes.push(file.name);
        continue;
      }
      if (file.size > maxAttachmentSize) {
        oversizedFiles.push(file.name);
        continue;
      }
      if (attachments.length + accepted.length >= maxAttachmentCount) {
        limitReached = true;
        continue;
      }

      existingKeys.add(view.key);
      accepted.push({ attachmentId: null, controller: null, error: null, file, key: view.key, progress: 0, status: "uploading", view });
    }

    if (accepted.length > 0) {
      updateAttachments((current) => [...current, ...accepted]);
      // 加入即开始上传，不等点发送：这样进度看得见，发送本身只带几个 id。
      for (const entry of accepted) void startUpload(entry);
      if (source === "long-text") toast.success(t("tickets.composer.longPasteConverted"));
    }
    const listFormatter = new Intl.ListFormat(intlLocale, { style: "long", type: "conjunction" });
    if (rejectedTypes.length > 0) toast.error(t("tickets.composer.unsupportedFiles", { files: listFormatter.format(rejectedTypes) }));
    if (oversizedFiles.length > 0) {
      toast.error(
        t("tickets.composer.oversizedFiles", {
          files: listFormatter.format(oversizedFiles),
          maxSize: formatFileSize(maxAttachmentSize, intlLocale),
        }),
      );
    }
    if (duplicateCount > 0) toast.info(t("tickets.composer.duplicatesIgnored", { count: numberFormatter.format(duplicateCount) }));
    if (limitReached) toast.error(t("tickets.composer.attachmentLimit", { count: numberFormatter.format(maxAttachmentCount) }));
  };

  /** 撤掉一个附件：在传的中止，传完的连服务端那份草稿一起删掉。 */
  const removeAttachment = (entry: QueuedAttachment) => {
    entry.controller?.abort();
    updateAttachments((current) => current.filter((attachment) => attachment.key !== entry.key));
    if (entry.attachmentId) {
      // 服务端那份删不掉也不拦着用户继续写——留下的是一个不会出现在时间线里的草稿附件。
      void ticketApi.deleteAttachment(ticketId, entry.attachmentId).catch(() => undefined);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []), "picker");
    event.target.value = "";
  };

  const handleFileDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    fileDragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleFileDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleFileDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
    if (fileDragDepthRef.current === 0) setIsDraggingFiles(false);
  };

  const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    fileDragDepthRef.current = 0;
    setIsDraggingFiles(false);
    addFiles(Array.from(event.dataTransfer.files), "drop");
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    const pastedText = event.clipboardData.getData("text/plain");
    const lineCount = pastedText.length > 0 ? pastedText.split(/\r?\n/).length : 0;
    const shouldConvertText = pastedText.length > longPasteCharacterLimit || lineCount > longPasteLineLimit;
    if (files.length === 0 && !shouldConvertText) return;

    event.preventDefault();
    if (shouldConvertText) {
      const textFile = new File([pastedText], t("tickets.composer.pastedTextFileName"), { type: "text/plain", lastModified: Date.now() });
      addFiles([textFile, ...files], "long-text");
      return;
    }

    addFiles(files, "paste");
  };

  const isUploading = attachments.some((attachment) => attachment.status === "uploading");
  const hasFailedUpload = attachments.some((attachment) => attachment.status === "failed");
  const readyAttachments = attachments.filter((attachment) => attachment.status === "ready");

  const submit = async () => {
    if (isReadOnly) return;
    if (!content.trim() && readyAttachments.length === 0) return;
    // 还在传或传失败时不放行：静默把没传上去的文件丢掉，比让用户多等两秒糟得多。
    if (isUploading || hasFailedUpload) return;

    const serializedContent = serializeTicketMentions(content, pickedMentions).trim();
    const activeMentionById = new Map<string, { agentId: string; displayName: string }>();
    for (const pickedMention of pickedMentions) {
      if (serializedContent.includes(mentionToken(pickedMention.agentId))) {
        activeMentionById.set(pickedMention.agentId, { agentId: pickedMention.agentId, displayName: pickedMention.displayName });
      }
    }
    const draft: InternalNoteDraft = {
      content: serializedContent,
      attachmentIds: readyAttachments.map((attachment) => attachment.attachmentId as string),
      attachments: readyAttachments.map((attachment) => attachment.view),
      // 同一人提及多次只需一份元数据；不同 ID 即使重名也分别保留。
      mentions: [...activeMentionById.values()],
    };

    // 时间线会立即乐观展示本条消息，编辑器则马上腾空，允许继续输入下一条。
    setContent("");
    updateAttachments(() => []);
    setPickedMentions([]);
    setMention(null);
    setActiveMentionIndex(0);
    setComposerScrollTop(0);
    mentionAnchorPositionRef.current = 0;

    try {
      await onSubmit(draft);
    } catch {
      // 页面负责展示失败提示；旧草稿不回填，避免覆盖发送期间新输入的内容。
      return;
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      void submit();
      return;
    }

    if (!mention) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setMention(null);
      return;
    }
    if (flatCandidates.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      // 上下键跨组连续移动，不用管候选被分在哪一组里。
      setActiveMentionIndex((current) => (Math.min(current, flatCandidates.length - 1) + direction + flatCandidates.length) % flatCandidates.length);
      return;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      const selectedCandidate = flatCandidates[activeIndex] ?? flatCandidates[0];
      if (selectedCandidate) insertMention(selectedCandidate);
    }
  };

  // 保留组件内的草稿：状态变更失败回滚或重新打开工单后，仍能继续编辑。
  if (isReadOnly) {
    return (
      <div className="pt-2 pb-2">
        <Alert variant="destructive" className="mx-auto max-w-3xl" role="status">
          <Icon icon="tabler:lock" className="text-current" />
          <AlertDescription>{t(status === TicketStatus.Closed ? "tickets.composer.closedHint" : "tickets.composer.cancelledHint")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="pt-2 pb-2">
      <div className="mx-auto flex w-full max-w-3xl items-start gap-3">
        <Avatar className="mt-10 hidden size-8 sm:flex" title={agent.displayName}>
          {agent.avatarUrl ? <AvatarImage alt={agent.displayName} src={agent.avatarUrl} /> : null}
          <AvatarFallback>{initialsOf(agent.displayName)}</AvatarFallback>
        </Avatar>
        <Card
          className="relative min-w-0 flex-1 gap-0 overflow-hidden bg-background py-0 shadow-sm"
          onDragEnter={handleFileDragEnter}
          onDragLeave={handleFileDragLeave}
          onDragOver={handleFileDragOver}
          onDrop={handleFileDrop}
        >
          {isDraggingFiles ? (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border border-dashed border-primary bg-background/90 text-sm font-medium text-primary backdrop-blur-[1px]">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5">
                <Icon className="size-4" icon="tabler:file-upload" />
                {t("tickets.composer.dropFiles")}
              </span>
            </div>
          ) : null}
          <div className="px-3 py-2">
            <div className="flex min-h-6 flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                <Icon className="size-4 text-primary" icon="tabler:lock" />
                {t("tickets.activity.internalMessage")}
              </span>
              <span aria-hidden="true" className="h-4 w-px bg-border" />
              <span className="text-muted-foreground">
                {t("tickets.composer.attachmentCount", {
                  count: numberFormatter.format(attachments.length),
                  max: numberFormatter.format(maxAttachmentCount),
                })}
              </span>
              {attachments.length === 0 ? <span className="text-muted-foreground">{t("tickets.composer.pasteHint")}</span> : null}
              {/* 加入即上传，附件在这一行就能看出传完了没有，不用等点发送才知道。 */}
              {isUploading ? (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Loader className="text-current" label={t("tickets.composer.attachmentsUploading")} size={14} />
                  {t("tickets.composer.uploading")}
                </span>
              ) : null}
            </div>
            {attachments.length > 0 ? (
              <ul aria-label={t("tickets.composer.pendingAttachmentsAria")} className="mt-1.5 flex flex-wrap gap-1.5">
                {attachments.map((attachment) => {
                  const hasFailed = attachment.status === "failed";
                  const failureReason = attachment.error ?? t("tickets.composer.uploadFailed");
                  // 文件就在本地，上传没传完也能看——想确认自己拖错了没有，不必先等它传完。
                  const canPreview = isPreviewableAttachment(attachment.view);

                  return (
                    <li
                      className={cn(
                        "relative inline-flex max-w-full items-center overflow-hidden rounded-md bg-card text-xs ring-1",
                        hasFailed ? "ring-destructive/50" : "ring-border",
                      )}
                      key={attachment.key}
                    >
                      {/* 上传进度直接铺在 chip 底色上：不额外占一行，也不用另引进度条组件。 */}
                      {attachment.status === "uploading" ? (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-y-0 left-0 bg-primary/10 transition-[width] duration-200"
                          style={{ width: `${attachment.progress}%` }}
                        />
                      ) : null}
                      <button
                        aria-label={canPreview ? t("tickets.attachment.previewAria", { name: attachment.view.name }) : attachment.view.name}
                        className="relative flex min-w-0 items-center gap-1.5 rounded-l-md px-2 py-1 text-left enabled:hover:bg-muted enabled:focus-visible:outline-none enabled:focus-visible:ring-2 enabled:focus-visible:ring-ring"
                        disabled={!canPreview}
                        type="button"
                        onClick={() => setPreviewAttachment(attachment.view)}
                      >
                        {attachment.status === "uploading" ? (
                          <Loader
                            className="shrink-0 text-muted-foreground"
                            label={t("tickets.composer.fileUploading", { name: attachment.view.name })}
                            size={14}
                          />
                        ) : (
                          <Icon
                            className={cn("size-3.5 shrink-0", hasFailed ? "text-destructive" : "text-muted-foreground")}
                            icon={attachmentIcon(attachment.view)}
                          />
                        )}
                        <span className="max-w-40 truncate">{attachment.view.name}</span>
                        <span className="shrink-0 text-muted-foreground">
                          {attachment.status === "uploading"
                            ? percentFormatter.format(attachment.progress / 100)
                            : formatFileSize(attachment.view.size, intlLocale)}
                        </span>
                        {canPreview ? <Icon className="size-3.5 shrink-0 text-muted-foreground" icon="tabler:eye" /> : null}
                      </button>
                      {hasFailed ? (
                        <>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  aria-label={t("tickets.composer.uploadFailureAria", { reason: failureReason })}
                                  className="relative inline-flex self-stretch items-center px-1 text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  type="button"
                                >
                                  <Icon className="size-3.5" icon="tabler:info-circle" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top">{failureReason}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <button
                            aria-label={t("tickets.composer.retryUploadAria", { name: attachment.view.name })}
                            className="relative inline-flex self-stretch items-center px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            type="button"
                            onClick={() => void startUpload(attachment)}
                          >
                            <Icon className="size-3.5" icon="tabler:refresh" />
                          </button>
                        </>
                      ) : null}
                      <button
                        aria-label={t("tickets.composer.removeAttachmentAria", { name: attachment.view.name })}
                        className="relative inline-flex self-stretch items-center rounded-r-md px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        type="button"
                        onClick={() => removeAttachment(attachment)}
                      >
                        <Icon className="size-3.5" icon="tabler:x" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          <div className="bg-white dark:bg-input/30 rounded-xl border -m-px mt-0">
            <Popover open={mention !== null} onOpenChange={(open) => !open && setMention(null)}>
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 overflow-hidden px-4 py-3 text-base leading-5 text-foreground md:text-sm"
                >
                  <div className="whitespace-pre-wrap wrap-break-word" style={{ transform: `translateY(-${composerScrollTop}px)` }}>
                    {renderComposerContent(content, pickedMentions, mention?.start ?? mentionAnchorPositionRef.current)}
                    {content.endsWith("\n") ? "​" : null}
                  </div>
                </div>
                <Textarea
                  aria-activedescendant={mention && flatCandidates.length > 0 ? `mention-option-${flatCandidates[activeIndex]?.agentId}` : undefined}
                  aria-autocomplete="list"
                  aria-controls={mention ? "mention-candidates" : undefined}
                  aria-expanded={mention !== null}
                  aria-label={t("tickets.composer.contentAria")}
                  className="relative min-h-20 max-h-40 resize-none overflow-y-auto rounded-none border-0 bg-transparent px-4 py-3 leading-5 text-transparent caret-foreground shadow-none selection:bg-primary/20 selection:text-transparent focus-visible:ring-0 disabled:bg-transparent disabled:opacity-100 dark:bg-transparent dark:disabled:bg-transparent"
                  placeholder={t("tickets.composer.placeholder")}
                  ref={textareaRef}
                  value={content}
                  onChange={(event) => {
                    const nextContent = event.target.value;
                    setPickedMentions((current) => reconcileTicketMentionOccurrences(content, nextContent, current));
                    setContent(nextContent);
                    updateMention(nextContent, event.target.selectionStart);
                  }}
                  onClick={(event) => updateMention(event.currentTarget.value, event.currentTarget.selectionStart)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onScroll={(event) => setComposerScrollTop(event.currentTarget.scrollTop)}
                  onSelect={(event) => updateMention(event.currentTarget.value, event.currentTarget.selectionStart)}
                />
              </div>
              <PopoverContent
                align="start"
                className="w-80 gap-0 p-1"
                id="mention-candidates"
                role="listbox"
                side="top"
                onCloseAutoFocus={(event) => event.preventDefault()}
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                <p className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {t("tickets.composer.mention.title")}
                  {isSearching ? <Loader className="text-current" label={t("tickets.composer.mention.searching")} size={14} /> : null}
                </p>

                {isLoading ? (
                  <p className="flex items-center justify-center gap-2 px-2 py-5 text-sm text-muted-foreground">
                    <Loader className="text-current" label={t("tickets.composer.mention.loadingCandidates")} size={16} />
                    {t("tickets.composer.mention.loading")}
                  </p>
                ) : isError ? (
                  <p className="px-2 py-5 text-center text-sm text-muted-foreground">{t("tickets.composer.mention.error")}</p>
                ) : flatCandidates.length === 0 ? (
                  <p className="px-2 py-5 text-center text-sm text-muted-foreground">{t("tickets.composer.mention.empty")}</p>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {groups.map((group) => {
                      const label = groupLabel(group, t);
                      return (
                        // biome-ignore lint/a11y/useSemanticElements: listbox 里分组的正解就是 role="group"，<fieldset> 会破坏 listbox 结构
                        <div aria-label={label} className="pb-0.5" key={group.teamId ?? "ungrouped"} role="group">
                          <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
                          {group.agents.map((candidate) => {
                            const index = indexByAgentId.get(candidate.agentId) ?? 0;
                            const badge = candidateBadge(candidate, t);
                            const hasDuplicateName = duplicateCandidateNames.has(candidate.displayName);

                            return (
                              <button
                                aria-selected={index === activeIndex}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left outline-none hover:bg-muted focus-visible:bg-muted aria-selected:bg-muted"
                                id={`mention-option-${candidate.agentId}`}
                                key={candidate.agentId}
                                role="option"
                                type="button"
                                onClick={() => insertMention(candidate)}
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseEnter={() => setActiveMentionIndex(index)}
                              >
                                <Avatar size="sm">
                                  {candidate.avatarUrl ? <AvatarImage alt="" src={candidate.avatarUrl} /> : null}
                                  <AvatarFallback>{candidate.displayName.slice(0, 1)}</AvatarFallback>
                                </Avatar>
                                <span className="min-w-0 flex-1">
                                  <span className="flex items-center gap-1.5">
                                    <span className="truncate text-sm font-medium">{candidate.displayName}</span>
                                    {badge ? <Badge variant="secondary">{badge}</Badge> : null}
                                    {hasDuplicateName ? <Badge variant="outline">{t("tickets.composer.mention.duplicateName")}</Badge> : null}
                                  </span>
                                  {identityLabelOf(candidate) ? (
                                    <span className="block truncate text-xs text-muted-foreground">{identityLabelOf(candidate)}</span>
                                  ) : hasDuplicateName ? (
                                    <span className="block truncate text-xs text-muted-foreground">
                                      {t("tickets.composer.mention.identifier", { identifier: candidate.agentId.slice(-6) })}
                                    </span>
                                  ) : null}
                                </span>
                              </button>
                            );
                          })}
                          {/* 服务端每组只给前几个，剩下的靠继续输入收窄，不做翻页。 */}
                          {group.hasMore ? <p className="px-2 pb-1 text-[11px] text-muted-foreground">{t("tickets.composer.mention.hasMore")}</p> : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-1 px-2 py-2">
              <input accept={fileInputAccept} className="sr-only" multiple ref={fileInputRef} type="file" onChange={handleFileChange} />
              <Button
                aria-label={t("tickets.composer.addAttachmentAria")}
                size="icon-sm"
                title={t("tickets.composer.supportedFilesTitle", { maxSize: formatFileSize(maxAttachmentSize, intlLocale) })}
                type="button"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon icon="tabler:paperclip" />
              </Button>
              <Button
                aria-label={t("tickets.composer.mentionCollaboratorAria")}
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={openMentionMenu}
                onPointerDown={(event) => event.preventDefault()}
              >
                <Icon icon="tabler:at" />
              </Button>
              <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">
                {t(
                  isUploading
                    ? "tickets.composer.attachmentsUploading"
                    : hasFailedUpload
                      ? "tickets.composer.failedUploadHint"
                      : "tickets.composer.submitShortcut",
                )}
              </span>
              <Button
                aria-label={t("tickets.composer.submitAria")}
                className="ml-auto rounded-full"
                disabled={(!content.trim() && readyAttachments.length === 0) || isUploading || hasFailedUpload}
                size="icon"
                title={
                  isUploading ? t("tickets.composer.uploadInProgressTitle") : hasFailedUpload ? t("tickets.composer.resolveFailedUploadsTitle") : undefined
                }
                type="button"
                onClick={() => void submit()}
              >
                {isUploading ? (
                  <Loader className="text-current" label={t("tickets.composer.attachmentsUploading")} size={16} />
                ) : (
                  <Icon icon="tabler:arrow-up" />
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>
      {/* 编辑器里的附件都还在本地，预览直接读 File，不用回服务端取。 */}
      <AttachmentPreviewDialog attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    </div>
  );
}
